import React, { useState } from 'react';
import { IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { useBucket } from '../contexts/BucketContext';
import { ChatMessage, VideoInfo } from '../types/types';

interface SaveMessageButtonProps {
  message: ChatMessage;
  videoInfo: VideoInfo;
}

const SaveMessageButton: React.FC<SaveMessageButtonProps> = ({ message, videoInfo }) => {
  const { savedAnswers, addToBucket, removeFromBucket } = useBucket();
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Check if this message is already saved
  const isSaved = savedAnswers.some(answer => 
    answer.message.id === message.id && answer.videoInfo.uuid === videoInfo.uuid
  );

  const handleToggleSave = () => {
    if (isSaved) {
      // Find the saved answer with this message ID
      const savedAnswer = savedAnswers.find(answer => 
        answer.message.id === message.id && answer.videoInfo.uuid === videoInfo.uuid
      );
      
      if (savedAnswer) {
        removeFromBucket(savedAnswer.id);
        setNotification({
          open: true,
          message: 'Antwort wurde aus der Sammlung entfernt',
          severity: 'info'
        });
      }
    } else {
      addToBucket(message, videoInfo);
      setNotification({
        open: true,
        message: 'Antwort wurde zur Sammlung hinzugefügt',
        severity: 'success'
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  // Only show for bot messages
  if (message.sender !== 'bot' || message.isTypingIndicator) {
    return null;
  }

  return (
    <>
      <Tooltip title={isSaved ? "Aus Sammlung entfernen" : "Zur Sammlung hinzufügen"}>
        <IconButton 
          onClick={handleToggleSave}
          size="small"
          color={isSaved ? "primary" : "default"}
          sx={{ 
            opacity: 0.7,
            '&:hover': {
              opacity: 1
            }
          }}
        >
          {isSaved ? <BookmarkIcon fontSize="small" /> : <BookmarkBorderIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      <Snackbar 
        open={notification.open} 
        autoHideDuration={2000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SaveMessageButton;
